import { useHasPermission } from "@dexteel/mesf-core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useState } from "react";

import type { SectionSpec } from "@/shared/models/SectionSpec";
import {
	replaceSectionSpecFile,
	uploadSectionSpecImage,
} from "@/shared/repositories/SectionSpecRepository";
import {
	SECTION_SPEC_FILE_KIND,
	type SectionSpecFileKind,
} from "@/shared/models/SectionSpec";
import {
	applySectionSpecRotation,
	getSectionSpecRotationAngle,
	type SectionSpecFileChange,
	type SectionSpecRotation,
} from "../models/SectionSpecRotation";

type SectionSpecFileHeader = Pick<
	SectionSpec,
	| "ProfileSpecId"
	| "SectionId"
	| "MillDrawingUploadFileId"
	| "DimensionsReferenceUploadFileId"
>;

export const useCanReplaceSectionSpecFile = () =>
	useHasPermission()("DIM.ReplaceProfileSpecFile");

export const replaceSectionSpecFileChange = (
	spec: SectionSpecFileHeader,
	kind: SectionSpecFileKind,
	change: SectionSpecFileChange,
	uploadFileId: number,
) =>
	replaceSectionSpecFile({
		profileSpecId: spec.ProfileSpecId,
		fileKind: kind,
		uploadFileId,
		expectedUploadFileId:
			(kind === SECTION_SPEC_FILE_KIND.MILL_DRAWING
				? spec.MillDrawingUploadFileId
				: spec.DimensionsReferenceUploadFileId) ?? null,
		rotationAngle: getSectionSpecRotationAngle(change),
	});

export const saveSectionSpecFileChange = async (
	spec: SectionSpecFileHeader,
	kind: SectionSpecFileKind,
	change: SectionSpecFileChange,
) => {
	const upload = await uploadSectionSpecImage(change.file);
	await replaceSectionSpecFileChange(spec, kind, change, upload.uploadFileId);
};

interface UseSectionSpecFileRotationOptions {
	spec: SectionSpecFileHeader;
	kind: SectionSpecFileKind;
	blob: Blob | null;
	fileName: string | null;
	onSaved?: () => Promise<void>;
}

export const useSectionSpecFileRotation = ({
	spec,
	kind,
	blob,
	fileName,
	onSaved,
}: UseSectionSpecFileRotationOptions) => {
	const queryClient = useQueryClient();
	const canReplace = useCanReplaceSectionSpecFile();

	const [change, setChange] = useState<SectionSpecFileChange | null>(null);
	const [rotating, setRotating] = useState(false);

	const baseName = fileName?.split(/[\\/]/).pop() ?? null;
	const canRotate = blob !== null && baseName !== null && canReplace;

	const saveMutation = useMutation({
		mutationFn: (fileChange: SectionSpecFileChange) =>
			saveSectionSpecFileChange(spec, kind, fileChange),
		onSuccess: async () => {
			await Promise.all([
				queryClient.invalidateQueries({
					queryKey: ["section-spec", spec.SectionId],
				}),
				queryClient.invalidateQueries({
					queryKey: ["section-spec-version", spec.ProfileSpecId],
				}),
				onSaved?.(),
			]);
			setChange(null);
			enqueueSnackbar("File rotated", { variant: "success" });
		},
		onError: (error: Error) =>
			enqueueSnackbar(error.message, { variant: "error" }),
	});

	const rotate = async (rotation: SectionSpecRotation) => {
		if (blob === null || baseName === null) return;
		setRotating(true);
		try {
			setChange(
				await applySectionSpecRotation(
					change,
					{ blob, fileName: baseName },
					rotation,
				),
			);
		} catch (error) {
			enqueueSnackbar(
				error instanceof Error
					? error.message
					: "The file could not be rotated",
				{ variant: "error" },
			);
		} finally {
			setRotating(false);
		}
	};

	return {
		canRotate,
		change,
		rotating,
		saving: saveMutation.isPending,
		rotate: (rotation: SectionSpecRotation) => void rotate(rotation),
		save: () => {
			if (change !== null) saveMutation.mutate(change);
		},
		discard: () => setChange(null),
	};
};
